import config from "../config";
import {
  Ri360AutoCalibrationState,
  CartesianCoordinateHotelConfig,
  Ri360AutoCalibrationFineTuningResult,
  ExportAutoCalibrationPsrCameraResult,
  ExportAutoCalibrationProximityResult,
} from "../Model/Calibration";
import { ELightPath } from "../Model/CaptureProfile";
import { ECarrier } from "../Model/ECarrier";
import { EModuleType } from "../Model/EModuleType";
import { EMoveArmDirection } from "../Model/EMoveImagingArmButton";
import {
  BinningFlatFieldCalibration,
  ImagerCalibration,
} from "../Model/ImagerCalibration";
import { EPsrPosition, ESpeed } from "../Model/ModuleState";
import {
  StorageCalibrationConfig,
  StorageCalibrations,
} from "../Model/StorageConfig";
import {
  CartesianVector,
  CartesianWaypoint,
  EArmType,
  EWaypointDirection,
  CylindricalVector,
  CylindricalWaypoint,
  ModuleWaypoint,
} from "../Model/Waypoint";
import { SlotCorrectionResult, SlotAddress } from "../Model/SlotCorrection";
import {
  sendDeleteRequest,
  sendGetRequest,
  sendPostRequest,
  sendPutRequest,
  sendPostRequestReturnValue,
  sendFileRequest,
} from "./../Utility/FetchUtils";
import { CartesianCoordinate } from "../Model/Coordinate";
import { sendDownloadTextRequest } from "../Utility/FetchUtils";
import { EFootprint } from "../Model/PlateType";
import { Hand2ThetaCorrectionResult } from "../Model/Hand2ThetaCorrectionResult";
import {
  Ri1000PsrAlignmentSetting,
  ERi1000PsrAlignmentLocation,
} from "../Model/PsrAlignmentCalibrationSetting";
import PsrAlignmentCalibrationResult from "../Model/PsrAlignmentCalibrationResult";
import Hand2OffsetCalibrationResult from "../Model/Hand2OffsetCalibrationResult";
import Ri1000StorageHeightCalibrationResult from "../Model/Ri1000StorageHeightCalibrationResult";
import { HotelCalibrationSetting } from "../Model/HotelCalibrationSetting";
import HotelcalibrationResult from "../Model/HotelCalibrationResult";
import { PlateAddress } from "../Model/PlateAddress";
import fileDownload from "js-file-download";
import HotelCalibrationResult from "../Model/HotelCalibrationResult";
export interface RunWaypointPayload {
  cartesianWaypoint?: CartesianWaypoint;
  cylindricalWaypoint?: CylindricalWaypoint;
  direction: EWaypointDirection;
}

export default class CalibrationApi {
  static setPickupMotor(moduleIdentifier: string, isExtended: boolean) {
    const url = `${this.BASE_URL}/psr/:set-pickup-motor/${moduleIdentifier}/${isExtended}`;
    return sendPostRequest(url);
  }
  static setPlateBump(moduleIdentifier: string, isActivated: boolean) {
    const url = `${this.BASE_URL}/imager/:set-plate-bump/${moduleIdentifier}/${isActivated}`;
    return sendPostRequest(url);
  }

  static setElectromagnet(moduleIdentifier: string, isActivated: boolean) {
    const url = `${this.BASE_URL}/psr/:set-electromagnet/${moduleIdentifier}/${isActivated}`;
    return sendPostRequest(url);
  }

  static findCarrierMaxPosition(moduleIdentifier: string) {
    const url = `${this.BASE_URL}/psr/:find-carrier-max-position/${moduleIdentifier}`;
    return sendPostRequest(url);
  }

  static ExpandCarrier(moduleIdentifier: string, carrier: ECarrier) {
    const url =
      this.BASE_URL +
      "/carrier/" +
      moduleIdentifier +
      "/" +
      carrier +
      "/:expand";
    return sendPostRequest(url);
  }

  static runFindSafePosition(moduleIdentifier: string){
    const url = `${this.BASE_URL}/psr/:run-find-x-safe-position/${moduleIdentifier}`;
    return sendPostRequest(url);
  }

  static stopFindSafePosition(moduleIdentifier: string){
    const url = `${this.BASE_URL}/psr/:stop-find-x-safe-position/${moduleIdentifier}`;
    return sendPostRequest(url);
  }
  static moveToSafePosition(moduleIdentifier: string,psrPosition : EPsrPosition){
    const url = `${this.BASE_URL}/psr/:move-to-x-safe-position/${moduleIdentifier}/${psrPosition}`;
    return sendPostRequest(url);
  }
  static BASE_URL = config.SERVICE_URL + "/Calibration";

  static getFlatFieldCalibrationOnCurrentBinning(
    imagerIdentifier: string,
    lightPath: ELightPath,
    minPixelValue: number
  ) {
    const url = `${this.BASE_URL}/imager/flat-field-calibration/${imagerIdentifier}/${lightPath}/${minPixelValue}`;
    return sendGetRequest<BinningFlatFieldCalibration>(url);
  }

  static getImagerCalibration(imagerIdentifier: string) {
    const url = this.BASE_URL + "/imager/" + imagerIdentifier;
    return sendGetRequest<ImagerCalibration>(url);
  }

  static updateImagerCalibration(
    calibration: ImagerCalibration,
    imagerIdentifier: string
  ) {
    const url = this.BASE_URL + "/imager/" + imagerIdentifier;
    return sendPutRequest(url, calibration);
  }

  static getWaypoint() {
    const url = this.BASE_URL + "/waypoint";
    return sendGetRequest<ModuleWaypoint[]>(url);
  }

  static GetWaypointForHotel(
    moduleIdentifier: string,
    waypointName: string,
    carrier: ECarrier,
    hotelNumber: number
  ) {
    const url =
      this.BASE_URL +
      "/waypoint-hotel/" +
      moduleIdentifier +
      "/" +
      waypointName +
      "/" +
      carrier +
      "/" +
      hotelNumber;
    return sendGetRequest<ModuleWaypoint[]>(url);
  }

  static GetWaypointForHotelWithOverride(
    moduleIdentifier: string,
    waypointName: string,
    carrier: ECarrier,
    hotelNumber: number,
    storageCalibration: StorageCalibrations
  ) {
    const url =
      this.BASE_URL +
      "/waypoint-hotel-override/" +
      moduleIdentifier +
      "/" +
      waypointName +
      "/" +
      carrier +
      "/" +
      hotelNumber;
    return sendPostRequestReturnValue<ModuleWaypoint[]>(
      url,
      storageCalibration
    );
  }

  static getStorageCalibration() {
    const url = this.BASE_URL + "/storage";
    return sendGetRequest<StorageCalibrationConfig>(url);
  }

  static GetAutoCalibrationResult() {
    const url = this.BASE_URL + "/auto-calibration-result";
    return sendGetRequest<Ri360AutoCalibrationState[]>(url);
  }

  static GetVectorDeviation() {
    const url = this.BASE_URL + "/vector-deviation";
    return sendGetRequest<{ [index: string]: CartesianCoordinate }>(url);
  }

  static updateStorageCalibration(storageCalibrations: StorageCalibrations) {
    const url = this.BASE_URL + "/storage";
    return sendPutRequest(url, storageCalibrations);
  }

  static runWaypoint(moduleIdentifier: string, payload: RunWaypointPayload) {
    const url = this.BASE_URL + "/waypoint/" + moduleIdentifier + "/:run";
    return sendPostRequest(url, payload);
  }

  static updateWaypoint(
    carrier: ECarrier,
    hotelNumber: number,
    slotNumber: number,
    waypoint: ModuleWaypoint
  ) {
    const url =
      this.BASE_URL + `/waypoint/${carrier}/${hotelNumber ?? 1}/${slotNumber}`;
    return sendPutRequest(url, waypoint);
  }

  static createNewWaypoint(waypoint: ModuleWaypoint) {
    const url = this.BASE_URL + `/waypoint`;
    return sendPostRequest(url, waypoint);
  }

  static deleteWaypoint(moduleIdentifier: string, waypointName: string) {
    const url =
      this.BASE_URL + "/waypoint/" + moduleIdentifier + "/" + waypointName;
    return sendDeleteRequest(url);
  }

  static cylindricalArmMoveAbsolute(
    moduleIdentifier: string,
    armType: EArmType,
    vector: CylindricalVector
  ) {
    const url = `${this.BASE_URL}/:cylindrical-arm-move-absolute/${moduleIdentifier}/${armType}`;
    return sendPostRequest(url, vector);
  }

  static cartesianArmMoveAbsolute(
    moduleIdentifier: string,
    armType: EArmType,
    vector: CartesianVector
  ) {
    const url = `${this.BASE_URL}/:cartesian-arm-move-absolute/${moduleIdentifier}/${armType}`;
    return sendPostRequest(url, vector);
  }

  static armMoveToTravelLimit(
    moduleIdentifier: string,
    armType: EArmType,
    direction: EMoveArmDirection,
    speed: ESpeed
  ) {
    const url = `${this.BASE_URL}/:arm-move-to-travel-limit/${moduleIdentifier}/${armType}/${direction}/${speed}`;
    return sendPostRequest(url);
  }

  static armStop(moduleIdentifier: string) {
    const url = `${this.BASE_URL}/:arm-stop/${moduleIdentifier}`;
    return sendPostRequest(url);
  }

  static runStorageAutoCalibration(
    moduleIdentifier: string,
    moduleType: EModuleType,
    carriers: ECarrier[]
  ) {
    const url = `${this.BASE_URL}/:run-storage-auto-calibration/${moduleIdentifier}/${moduleType}`;
    return sendPostRequest(url, carriers);
  }

  static stopStorageAutoCalibration(
    moduleIdentifier: string,
    moduleType: EModuleType
  ) {
    const url = `${this.BASE_URL}/:stop-storage-auto-calibration/${moduleIdentifier}/${moduleType}`;
    return sendPostRequest(url);
  }

  static discardStorageAutoCalibration(
    moduleIdentifier: string,
    moduleType: EModuleType
  ) {
    const url = `${this.BASE_URL}/:discard-storage-auto-calibration/${moduleIdentifier}/${moduleType}`;
    return sendPostRequest(url);
  }

  static saveStorageAutoCalibration(
    moduleIdentifier: string,
    moduleType: EModuleType
  ) {
    const url = `${this.BASE_URL}/:save-storage-auto-calibration/${moduleIdentifier}/${moduleType}`;
    return sendPostRequest(url);
  }

  static useManualCalibration(
    moduleIdentifier: string,
    moduleType: EModuleType,
    carriers: ECarrier[]
  ) {
    const url = `${this.BASE_URL}/:set-use-storage-manual-calibration/${moduleIdentifier}/${moduleType}`;
    return sendPostRequest(url, carriers);
  }

  static updateStorageAutoCalibration(
    carrier: ECarrier,
    autoCalibrationResult: CartesianCoordinateHotelConfig[]
  ) {
    const url = `${this.BASE_URL}/:update-storage-auto-calibration/${carrier}`;

    return sendPostRequest(url, autoCalibrationResult);
  }

  static exportAutoCalibrationCamera(
    carrier: ECarrier,
    footprint: EFootprint,
    autoCalibrationResult: Ri360AutoCalibrationFineTuningResult[]
  ) {
    const getExportAutoCalibrationPsrCameraData: ExportAutoCalibrationPsrCameraResult =
      {
        carrier: carrier,
        footprint: footprint,
        data: autoCalibrationResult,
      };
    const url = `${this.BASE_URL}/:exportPsrCamera/`;
    return sendPostRequestReturnValue<string>(
      url,
      getExportAutoCalibrationPsrCameraData
    ).then((date) => {
      const getUrl = this.BASE_URL + "/export/" + date;
      return sendDownloadTextRequest(getUrl, "AutoCalibarionExport.csv");
    });
  }
  static exportAutoCalibrationProximity(
    carrier: ECarrier,
    footprint: EFootprint,
    autoCalibrationResult: Ri360AutoCalibrationState[]
  ) {
    const getExportAutoCalibrationProximityData: ExportAutoCalibrationProximityResult =
      {
        carrier: carrier,
        footprint: footprint,
        data: autoCalibrationResult,
      };
    const url = `${this.BASE_URL}/:exportProximity/`;
    return sendPostRequestReturnValue<string>(
      url,
      getExportAutoCalibrationProximityData
    ).then((date) => {
      const getUrl = this.BASE_URL + "/export/" + date;
      return sendDownloadTextRequest(getUrl, "AutoCalibarionExport.csv");
    });
  }

  static GetSlotCorrectionResult() {
    const url = this.BASE_URL + "/slot-correction-result";
    return sendGetRequest<SlotCorrectionResult[]>(url);
  }

  static runSlotCorrection(
    psr_identifier: string,
    PlateAddress: PlateAddress[]
  ) {
    const url = `${this.BASE_URL}/:run-slot-correction/${psr_identifier}`;

    return sendPostRequest(url, PlateAddress);
  }

  static stopSlotCorrection(psr_identifier: string) {
    const url = `${this.BASE_URL}/:stop-slot-correction/${psr_identifier}`;
    return sendPostRequest(url);
  }

  static discardSlotCorrection(psr_identifier: string) {
    const url = `${this.BASE_URL}/:discard-slot-correction/${psr_identifier}`;
    return sendPostRequest(url);
  }

  static saveSlotCorrection(
    psr_identifier: string,
    sloCorrectionResult: SlotCorrectionResult[]
  ) {
    const url = `${this.BASE_URL}/:save-slot-correction/${psr_identifier}`;
    return sendPostRequest(url, sloCorrectionResult);
  }

  static deleteSlotCorrection(sloCorrectionResult: SlotCorrectionResult[]) {
    const url = `${this.BASE_URL}/slot-correction/`;
    return sendDeleteRequest(url, sloCorrectionResult);
  }

  static runHand2ThetaCorrection() {
    const url = `${this.BASE_URL}/:run-hand-2-theta-correction`;
    return sendPostRequest(url);
  }

  static stopHand2ThetaCorrection() {
    const url = `${this.BASE_URL}/:stop-hand-2-theta-correction`;
    return sendPostRequest(url);
  }

  static getHand2ThetaCorrectionResult() {
    const url = `${this.BASE_URL}/hand-2-theta-correction-result`;
    return sendGetRequest<Hand2ThetaCorrectionResult[]>(url);
  }

  static saveHand2ThetaCorrection() {
    const url = `${this.BASE_URL}/:save-hand-2-theta-correction`;
    return sendPostRequest(url);
  }

  static resetHand2ThetaCorrection() {
    const url = `${this.BASE_URL}/:reset-hand-2-theta-correction`;
    return sendPostRequest(url);
  }

  static revertHand2ThetaCorrection() {
    const url = `${this.BASE_URL}/:revert-hand-2-theta-correction`;
    return sendPostRequest(url);
  }

  static runPsrAlignmentCalibration(
    aligmentSetting: Ri1000PsrAlignmentSetting
  ) {
    const url = `${this.BASE_URL}/:run-psr-alignment-calibration`;
    return sendPostRequest(url, aligmentSetting);
  }

  static stopPsrAlignmentCalibration() {
    const url = `${this.BASE_URL}/:stop-psr-alignment-calibration`;
    return sendPostRequest(url);
  }

  static savePsrAlignmentCalibration() {
    const url = `${this.BASE_URL}/:save-psr-alignment-calibration`;
    return sendPostRequest(url);
  }

  static getPsrAlignmentCalibraionResult() {
    const url = `${this.BASE_URL}/psr-alignment-calibration-result/`;
    return sendGetRequest<PsrAlignmentCalibrationResult[]>(url);
  }

  static resetPsrAlignmentCalibration() {
    const url = `${this.BASE_URL}/:reset-psr-alignment-calibration`;
    return sendPostRequest(url);
  }

  static revertPsrAlignmentCalibration() {
    const url = `${this.BASE_URL}/:revert-psr-alignment-calibration`;
    return sendPostRequest(url);
  }

  static runHand2OffsetCalibration() {
    const url = `${this.BASE_URL}/:run-hand-2-offset-calibration`;
    return sendPostRequest(url);
  }

  static stopHand2OffsetCalibration() {
    const url = `${this.BASE_URL}/:stop-hand-2-offset-calibration`;
    return sendPostRequest(url);
  }

  static saveHand2OffsetCalibration() {
    const url = `${this.BASE_URL}/:save-hand-2-offset-calibration`;
    return sendPostRequest(url);
  }

  static getHand2OffsetCalibraionResult() {
    const url = `${this.BASE_URL}/hand-2-offset-calibration-result/`;
    return sendGetRequest<Hand2OffsetCalibrationResult[]>(url);
  }

  static resetHand2OffsetCalibration() {
    const url = `${this.BASE_URL}/:reset-hand-2-offset-calibration`;
    return sendPostRequest(url);
  }

  static revertHand2OffsetCalibration() {
    const url = `${this.BASE_URL}/:revert-hand-2-offset-calibration`;
    return sendPostRequest(url);
  }

  static runStorageHeightCalibration() {
    const url = `${this.BASE_URL}/:run-storage-height-calibration`;
    return sendPostRequest(url);
  }

  static stopStorageHeightCalibration() {
    const url = `${this.BASE_URL}/:stop-storage-height-calibration`;
    return sendPostRequest(url);
  }

  static saveStorageHeightCalibration() {
    const url = `${this.BASE_URL}/:save-storage-height-calibration`;
    return sendPostRequest(url);
  }

  static getStorageHeightCalibrationResult() {
    const url = `${this.BASE_URL}/storage-height-calibration-result/`;
    return sendGetRequest<Ri1000StorageHeightCalibrationResult>(url);
  }

  static resetStorageHeightCalibration() {
    const url = `${this.BASE_URL}/:reset-storage-height-calibration`;
    return sendPostRequest(url);
  }

  static runHotelCalibration(hotelCalibrationSetting: HotelCalibrationSetting) {
    const url = `${this.BASE_URL}/:run-hotel-auto-calibration`;
    return sendPostRequest(url, hotelCalibrationSetting);
  }

  static stopHotelCalibration() {
    const url = `${this.BASE_URL}/:stop-hotel-auto-calibration`;
    return sendPostRequest(url);
  }

  static getHotelCalibrationResult() {
    const url = `${this.BASE_URL}/hotel-auto-calibration-result`;
    return sendGetRequest<HotelCalibrationResult>(url);
  }

  static resetHotelCalibrationResult() {
    const url = `${this.BASE_URL}/:reset-hotel-auto-calibration`;
    return sendPostRequest(url);
  }

  static revertHotelCalibrationResult() {
    const url = `${this.BASE_URL}/:revert-hotel-auto-calibration`;
    return sendPostRequest(url);
  }

  static saveHotelCalibrationResult() {
    const url = `${this.BASE_URL}/:save-hotel-auto-calibration`;
    return sendPostRequest(url);
  }

  static exportHotelCalibrationData(fileName: string) {
    const url = `${this.BASE_URL}/export-hotel-calibration`;
    return sendDownloadTextRequest(url, fileName);
  }

  static importHotelCalibrationData(data: FormData) {
    const url = `${this.BASE_URL}/:import-hotel-calibration`;

    return sendFileRequest(url, data);
  }

  static setHotelCalibrationHotelReference(hotel: number) {
    const url = `${this.BASE_URL}/:set-hotel-reference`;
    return sendPostRequest(url, hotel);
  }
}
